import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useOverlayContext } from '@providers/OverlayProvider';
import OVERLAYS from '@constants/overlays';

import type { AiTreeSection } from './AiScripts/kind';
import {
  EditorFocus,
  EditorNodeKind,
  EditorSelectedNode,
} from './editorCategories';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface EditorModeContextValue {
  selectedNode: EditorSelectedNode;
  selectMapAndColors: () => void;
  selectSpot: (ueIdentifier: string) => void;
  selectHotspot: (hotspotId: string) => void;
  selectRegionScript: (trigger: string, scriptId: string | null) => void;
  selectMapLayout: () => void;
  selectBrandingEntity: (entityId: string) => void;
  clearSelection: () => void;

  activeFocus: EditorFocus;
  setActiveFocus: (focus: EditorFocus) => void;

  // Which section of the assistant (AI Scripts) focus the left panel shows:
  // 'scripts' (AI triggers + spots, unified) or 'knowledge'. Lives here (not
  // local to the tree) because the UE selection bridges flip it on spot clicks
  // and on entering a live AI trigger.
  aiTreeSection: AiTreeSection;
  setAiTreeSection: (section: AiTreeSection) => void;

  // When true, walking into an AI trigger (SYNC_DATA_UE.audioTrigger.aiTriggerId)
  // auto-opens the inspector for that trigger instead of letting the virtual
  // assistant (Noa) play. Editor-only convenience; default off.
  followLiveTrigger: boolean;
  setFollowLiveTrigger: (value: boolean) => void;

  // Teleporting to a trigger (AiInspectorHeader's "teleport to trigger")
  // closes the Inspector, but the resulting real in-world region-enter
  // updates aiTriggerName shortly after — with followLiveTrigger on, that
  // would otherwise re-select the same trigger and reopen the pane right
  // after it was closed. Set to the trigger name right before teleporting;
  // AiTriggerSelectionBridge checks it and skips exactly one matching
  // re-selection, then clears it.
  suppressedTriggerSelectRef: React.MutableRefObject<string | null>;
  suppressNextTriggerSelect: (trigger: string) => void;

  isSceneTreeCollapsed: boolean;
  toggleSceneTree: () => void;

  isQuickSwitcherOpen: boolean;
  openQuickSwitcher: () => void;
  closeQuickSwitcher: () => void;

  autosaveStatus: AutosaveStatus;
  setAutosaveStatus: (status: AutosaveStatus) => void;

  // Bumped whenever entity values are written from outside the inspector — the
  // CSV import. The open inspector watches it and reloads, so the panel does
  // not keep showing the values the import has just replaced.
  brandingEntitiesTick: number;
  bumpBrandingEntitiesTick: () => void;

  // The inspector holds an edit for 600 ms before saving it. An import writes
  // the same record, so it must not race that save: the inspector registers its
  // flush here while mounted, and the importer awaits it before its own write.
  registerBrandingEntityFlush: (flush: (() => Promise<void>) | null) => void;
  flushBrandingEntitySave: () => Promise<void>;

  // Recent in-world spot clicks (UE → hub via CUSTOMISATION_ITEM_SELECTED).
  // Used by the AI Scripts create popover to suggest regions the user has
  // recently interacted with. Capped + deduped; reset between editor sessions.
  recentSceneClicks: string[];
  registerSceneClick: (id: string) => void;
}

const EditorModeContext = createContext<EditorModeContextValue | null>(null);

interface EditorModeProviderProps {
  children: ReactNode;
  initialFocus?: EditorFocus;
}

const getDefaultSelectionForFocus = (
  focus: EditorFocus,
): EditorSelectedNode => {
  if (focus === 'environment') {
    return { kind: EditorNodeKind.MAP_AND_COLORS };
  }
  // Like environment, the map editor has exactly one thing to edit — the layout —
  // so open its inspector straight away rather than making the author click a tree
  // row to reveal the only panel the focus has.
  if (focus === 'mapEditor') {
    return { kind: EditorNodeKind.MAP_LAYOUT };
  }
  return null;
};

export const EditorModeProvider = ({
  children,
  initialFocus = 'environment',
}: EditorModeProviderProps) => {
  const [activeFocus, setActiveFocusState] =
    useState<EditorFocus>(initialFocus);
  const [aiTreeSection, setAiTreeSection] = useState<AiTreeSection>('scripts');
  const [followLiveTrigger, setFollowLiveTrigger] = useState<boolean>(false);
  const suppressedTriggerSelectRef = useRef<string | null>(null);
  const suppressNextTriggerSelect = useCallback((trigger: string) => {
    suppressedTriggerSelectRef.current = trigger;
  }, []);
  const [selectedNode, setSelectedNode] = useState<EditorSelectedNode>(() =>
    getDefaultSelectionForFocus(initialFocus),
  );
  const [isSceneTreeCollapsed, setSceneTreeCollapsed] = useState(false);
  const [isQuickSwitcherOpen, setQuickSwitcherOpen] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [recentSceneClicks, setRecentSceneClicks] = useState<string[]>([]);
  const [brandingEntitiesTick, setBrandingEntitiesTick] = useState(0);
  // A ref, not state: registering must not re-render the tree or change the
  // context value, which would remount the inspector that just registered.
  const brandingEntityFlushRef = useRef<(() => Promise<void>) | null>(null);

  const overlay = useOverlayContext();

  const closeExclusiveOverlays = useCallback(() => {
    overlay?.removeFromCurrentlyOpenOverlays?.(OVERLAYS.ADMIN_TOOLS);
    overlay?.removeFromCurrentlyOpenOverlays?.(OVERLAYS.PROFILE);
  }, [overlay]);

  const registerSceneClick = useCallback((id: string) => {
    if (!id) {
      return;
    }
    setRecentSceneClicks((prev) => {
      const trimmed = id.trim();
      const filtered = prev.filter((existing) => existing !== trimmed);
      // Newest first, capped to keep the create popover from growing unbounded.
      return [trimmed, ...filtered].slice(0, 8);
    });
  }, []);

  const selectMapAndColors = useCallback(() => {
    closeExclusiveOverlays();
    setSelectedNode({ kind: EditorNodeKind.MAP_AND_COLORS });
  }, [closeExclusiveOverlays]);

  const selectSpot = useCallback(
    (ueIdentifier: string) => {
      closeExclusiveOverlays();
      setSelectedNode({ kind: EditorNodeKind.SPOT, ueIdentifier });
    },
    [closeExclusiveOverlays],
  );

  const selectHotspot = useCallback(
    (hotspotId: string) => {
      closeExclusiveOverlays();
      setSelectedNode({ kind: EditorNodeKind.HOTSPOT, hotspotId });
    },
    [closeExclusiveOverlays],
  );

  const selectRegionScript = useCallback(
    (trigger: string, scriptId: string | null) => {
      closeExclusiveOverlays();
      setSelectedNode({
        kind: EditorNodeKind.REGION_SCRIPT,
        trigger,
        scriptId,
      });
    },
    [closeExclusiveOverlays],
  );

  const selectMapLayout = useCallback(() => {
    closeExclusiveOverlays();
    setSelectedNode({ kind: EditorNodeKind.MAP_LAYOUT });
  }, [closeExclusiveOverlays]);

  const selectBrandingEntity = useCallback(
    (entityId: string) => {
      closeExclusiveOverlays();
      setSelectedNode({ kind: EditorNodeKind.BRANDING_ENTITY, entityId });
    },
    [closeExclusiveOverlays],
  );

  const clearSelection = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const setActiveFocus = useCallback((focus: EditorFocus) => {
    setActiveFocusState(focus);
    setSelectedNode(getDefaultSelectionForFocus(focus));
  }, []);

  const toggleSceneTree = useCallback(() => {
    setSceneTreeCollapsed((prev) => !prev);
  }, []);

  const openQuickSwitcher = useCallback(() => {
    setQuickSwitcherOpen(true);
  }, []);

  const closeQuickSwitcher = useCallback(() => {
    setQuickSwitcherOpen(false);
  }, []);

  const bumpBrandingEntitiesTick = useCallback(() => {
    setBrandingEntitiesTick((prev) => prev + 1);
  }, []);

  const registerBrandingEntityFlush = useCallback(
    (flush: (() => Promise<void>) | null) => {
      brandingEntityFlushRef.current = flush;
    },
    [],
  );

  const flushBrandingEntitySave = useCallback(async () => {
    await brandingEntityFlushRef.current?.();
  }, []);

  const value = useMemo<EditorModeContextValue>(
    () => ({
      selectedNode,
      selectMapAndColors,
      selectSpot,
      selectHotspot,
      selectRegionScript,
      selectMapLayout,
      selectBrandingEntity,
      clearSelection,
      activeFocus,
      setActiveFocus,
      aiTreeSection,
      setAiTreeSection,
      followLiveTrigger,
      setFollowLiveTrigger,
      suppressedTriggerSelectRef,
      suppressNextTriggerSelect,
      isSceneTreeCollapsed,
      toggleSceneTree,
      isQuickSwitcherOpen,
      openQuickSwitcher,
      closeQuickSwitcher,
      autosaveStatus,
      setAutosaveStatus,
      brandingEntitiesTick,
      bumpBrandingEntitiesTick,
      registerBrandingEntityFlush,
      flushBrandingEntitySave,
      recentSceneClicks,
      registerSceneClick,
    }),
    [
      selectedNode,
      selectMapAndColors,
      selectSpot,
      selectHotspot,
      selectRegionScript,
      selectMapLayout,
      selectBrandingEntity,
      clearSelection,
      activeFocus,
      setActiveFocus,
      aiTreeSection,
      followLiveTrigger,
      suppressNextTriggerSelect,
      isSceneTreeCollapsed,
      toggleSceneTree,
      isQuickSwitcherOpen,
      openQuickSwitcher,
      closeQuickSwitcher,
      autosaveStatus,
      brandingEntitiesTick,
      bumpBrandingEntitiesTick,
      registerBrandingEntityFlush,
      flushBrandingEntitySave,
      recentSceneClicks,
      registerSceneClick,
    ],
  );

  return (
    <EditorModeContext.Provider value={value}>
      {children}
    </EditorModeContext.Provider>
  );
};

export const useEditorMode = (): EditorModeContextValue => {
  const ctx = useContext(EditorModeContext);
  if (!ctx) {
    throw new Error('useEditorMode must be used inside EditorModeProvider');
  }
  return ctx;
};
